import { useCallback, useEffect, useState } from 'react'
import { useClient } from '../../providers/supabase'
import { useAsyncAction } from '../../hooks/useAsyncAction'
import { useTotpEnrollment } from '../../hooks/useTotpEnrollment'

interface VerifiedFactor {
  id: string
  friendly_name?: string | null
}

export function TwoFactorSettings({ onEnabled }: { onEnabled?: () => void }) {
  const client = useClient()

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<Error | null>(null)
  const [verified, setVerified] = useState<VerifiedFactor | null>(null)

  const {
    pending,
    enrolling,
    enrollError,
    verifying,
    verifyError,
    startEnrollment,
    verify,
    cancel,
  } = useTotpEnrollment()

  const [code, setCode] = useState('')
  const [showSecret, setShowSecret] = useState(false)

  const refreshFactors = useCallback(async () => {
    const { data, error } = await client.auth.mfa.listFactors()
    if (error) {
      setLoadError(new Error(error.message, { cause: error }))
      setLoading(false)
      return
    }
    const v = (data?.totp || []).find((f) => f.status === 'verified')
    setVerified(v ? { id: v.id, friendly_name: v.friendly_name } : null)
    setLoading(false)
  }, [client])

  useEffect(() => {
    refreshFactors()
  }, [refreshFactors])

  const handleVerify = async () => {
    try {
      await verify(code)
      setCode('')
      await refreshFactors()
      onEnabled?.()
    } catch {
      // verifyError state is set by the hook.
    }
  }

  const cancelEnrollment = async () => {
    await cancel()
    setCode('')
  }

  const [disableState, disable] = useAsyncAction(async () => {
    if (!verified) { return }
    const confirmed = window.confirm(
      'Remove two-factor authentication? You will no longer need a code to sign in.'
    )
    if (!confirmed) { return }

    const { error } = await client.auth.mfa.unenroll({ factorId: verified.id })
    if (error) { throw error }
    await refreshFactors()
  }, [client, verified])

  if (loading) {
    return <div className="loading-bar"></div>
  }

  if (loadError) {
    return <div className="alert alert-danger" role="alert">{loadError.message}</div>
  }

  if (verified) {
    return <div>
      <p className="mb-2">
        <span className="badge bg-success me-2">Enabled</span>
        You'll need a 6-digit code from your authenticator app each time you sign in.
      </p>
      <button className="btn btn-outline-danger btn-sm"
        onClick={disable}
        disabled={disableState.loading}>
        {disableState.loading ? 'Removing…' : 'Remove two-factor authentication'}
      </button>
      {disableState.error && <p className="text-danger mt-2 mb-0">
        {disableState.error.message}
      </p>}
    </div>
  }

  if (pending) {
    return <div>
      <p>
        Scan the QR code with your authenticator app, then enter the 6-digit code it generates.
      </p>
      <div className="text-center my-3">
        <img src={pending.qrCode} alt="TOTP QR code"
          style={{ maxWidth: '200px', width: '100%', background: 'white', padding: '8px' }} />
      </div>

      <div className="mb-3">
        <button type="button" className="btn btn-link p-0"
          onClick={() => setShowSecret((v) => !v)}>
          {showSecret ? "Hide setup key" : "Can't scan? Show setup key"}
        </button>
        {showSecret && <div className="mt-2">
          <code className="d-inline-block p-2 bg-light border rounded user-select-all">
            {pending.secret}
          </code>
        </div>}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); handleVerify() }}>
        <input type="text" className="form-control"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          maxLength={6}
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />

        {verifyError && <p className="text-danger mt-2 mb-0">
          {verifyError.message}
        </p>}

        <div className="mt-3">
          <button type="submit" className="btn btn-primary"
            disabled={verifying || code.length !== 6}>
            {verifying ? 'Verifying…' : 'Verify and enable'}
          </button>
          <button type="button" className="btn btn-link"
            onClick={cancelEnrollment}
            disabled={verifying}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  }

  return <div>
    <p className="mb-2">
      <span className="badge bg-secondary me-2">Off</span>
      Add an authenticator app (Google Authenticator, Authy, 1Password, etc.) for an extra layer of security.
    </p>
    {enrollError && <div className="alert alert-danger" role="alert">
      {enrollError.message}
    </div>}
    <button className="btn btn-primary btn-sm"
      onClick={startEnrollment}
      disabled={enrolling}>
      {enrolling ? 'Setting up…' : 'Set up two-factor authentication'}
    </button>
  </div>
}
